import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, FileSpreadsheet, Upload, ClipboardCheck, Lock, Users, FileText, TrendingUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BU, TEMPLATE } from "@/constants/testIds";
import { OrgNode } from "@/components/OrgNode";
import { AvatarControls, SkillsSection, DocumentsSection, BUSkillsMatrix, ContactBlock } from "@/components/EmployeeExtras";
import MyPoliciesLazy from "@/pages/MyPolicies";
import Reports from "@/pages/Reports";

const STATUS_BADGE = {
  draft: ["Draft", "bg-zinc-50 text-zinc-700 border-zinc-200"],
  pending_owner: ["Pending Owner", "bg-amber-50 text-amber-800 border-amber-200"],
  pending_super: ["Pending Sign-off", "bg-violet-50 text-violet-800 border-violet-200"],
  approved: ["Locked", "bg-emerald-50 text-emerald-800 border-emerald-200"],
};

const STAGE_BADGE = {
  pending_manager: ["Pending Manager", "bg-amber-50 text-amber-800 border-amber-200"],
  pending_employee: ["Pending Employee", "bg-blue-50 text-blue-800 border-blue-200"],
  pending_final: ["Pending Final", "bg-violet-50 text-violet-800 border-violet-200"],
  completed: ["Completed", "bg-emerald-50 text-emerald-800 border-emerald-200"],
};

export default function BusinessUnitDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(null);
  const [templatesSubTab, setTemplatesSubTab] = useState("kpa"); // kpa | policies | sops
  const [bu, setBu] = useState(null);
  const [depts, setDepts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [editingDept, setEditingDept] = useState(null);
  const [creatingTpl, setCreatingTpl] = useState(false);
  const [importing, setImporting] = useState(false);
  const [blankSeed, setBlankSeed] = useState(null); // null | {rows: [...]}
  const [selectedTpls, setSelectedTpls] = useState(() => new Set());
  const [creatingReview, setCreatingReview] = useState(false);
  const [reviewUserFilter, setReviewUserFilter] = useState("all");
  const [reviewQuarterFilter, setReviewQuarterFilter] = useState("All");

  const [policies, setPolicies] = useState([]);

  const load = async () => {
    const [b, d, t, r, u, n, p] = await Promise.all([
      api.get(`/business-units/${id}`),
      api.get("/departments"),
      api.get("/templates"),
      api.get("/reviews"),
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/notifications").catch(() => ({ data: [] })),
      api.get(`/policies?business_unit_id=${id}`).catch(() => ({ data: [] })),
    ]);
    setBu(b.data);
    setDepts(d.data.filter((x) => x.business_unit_id === id));
    setTemplates(t.data.filter((x) => x.business_unit_id === id));
    setReviews(r.data.filter((x) => x.template_snapshot?.business_unit_id === id));
    setUsers(u.data);
    setNotifs(n.data);
    setPolicies(p.data);
    setSelectedTpls(new Set());
  };
  useEffect(() => { load();  }, [id]); // eslint-disable-line

  // canEdit covers structural BU actions (logo, edit BU, Position Profile mgmt) — Super Admin and the BU's own Owner.
  const canEdit = user?.role === "super_admin" || (user?.role === "bu_owner" && user?.business_unit_id === id);
  // canTemplate adds BU Manager for this BU — they can create / import / edit templates and start the approval workflow.
  const canTemplate = canEdit || (user?.role === "bu_manager" && user?.business_unit_id === id);
  // Plain users get a stripped-down view: only their own reviews, PIP, activity, policies + own profile.
  const isPlainUser = user?.role === "user";
  const isManager = user?.role === "bu_manager";

  // Full tab list; some are hidden for plain users below.
  const ALL_TABS = [
    { id: "dashboard", label: `Dashboard`, testId: "bu-tab-dashboard" },
    { id: "reports", label: `Reports`, testId: "bu-tab-reports" },
    { id: "employees", label: isPlainUser ? "My Profile" : "Employees", testId: "bu-tab-employees" },
    { id: "skills", label: `Skills Matrix`, testId: "bu-tab-skills" },
    { id: "organogram", label: `Organogram`, testId: "bu-tab-organogram" },
    { id: "departments", label: `Position Profiles (${depts.length})`, testId: BU.tabDepartments },
    { id: "templates", label: `Templates (${templates.length})`, testId: BU.tabTemplates },
    { id: "reviews", label: `Reviews (${reviews.length})`, testId: BU.tabReviews },
    { id: "pip", label: `PIPs`, testId: "bu-tab-pip" },
    { id: "whatif", label: `What-if`, testId: "bu-tab-whatif" },
    { id: "mine", label: `My Policies`, testId: "bu-tab-my-policies" },
    { id: "inbox", label: `BU Activity`, testId: "bu-tab-inbox" },
    { id: "my_activity", label: `My Activity`, testId: "bu-tab-my-activity" },
    { id: "users", label: `Users`, testId: "bu-tab-users" },
  ];
  // Plain user tab set — see only their own review/PIP/policies/activity + profile view.
  const USER_TABS = new Set(["employees", "reviews", "pip", "mine", "my_activity"]);
  const visibleTabs = isPlainUser ? ALL_TABS.filter((t) => USER_TABS.has(t.id)) : ALL_TABS;

  // Default tab per role.
  useEffect(() => {
    if (tab !== null) return;
    setTab(isPlainUser ? "employees" : "dashboard");
  }, [tab, isPlainUser]);

  const uname = (uid) => users.find((u) => u.id === uid)?.name || "—";
  const dname = (did) => depts.find((d) => d.id === did)?.name || "—";

  const saveDept = async (data) => {
    try {
      const body = { ...data, business_unit_id: id };
      if (data.id) await api.put(`/departments/${data.id}`, body);
      else await api.post("/departments", body);
      toast.success("Saved");
      setEditingDept(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delDept = async (did) => {
    if (!confirm("Delete this Position Profile?")) return;
    try { await api.delete(`/departments/${did}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!bu) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div>
      <button onClick={() => nav("/business-units")} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Business Units
      </button>

      <div className="mb-8 flex items-start gap-6">
        {bu.logo ? (
          <div className="flex flex-col items-center gap-2">
            <img src={bu.logo} alt={bu.name} className="max-h-20 max-w-[200px] object-contain" />
            {canEdit && (
              <div className="flex items-center gap-2">
                <label
                  data-testid="bu-logo-replace-btn"
                  title="Replace logo"
                  className="p-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50 cursor-pointer text-zinc-600 hover:text-zinc-900"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <input type="file" accept="image/*" className="hidden" data-testid="bu-logo-upload-input" onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 1024 * 1024) return toast.error("Logo must be under 1 MB");
                    const reader = new FileReader();
                    reader.onload = async () => {
                      try {
                        await api.put(`/business-units/${bu.id}`, { name: bu.name, description: bu.description || "", owner_id: bu.owner_id, logo: reader.result });
                        toast.success("Logo updated");
                        load();
                      } catch (err) { toast.error(formatApiError(err)); }
                    };
                    reader.readAsDataURL(f);
                  }} />
                </label>
                <button
                  data-testid="bu-logo-delete-btn"
                  title="Remove logo"
                  onClick={async () => {
                    if (!confirm("Remove this BU's logo?")) return;
                    try {
                      await api.put(`/business-units/${bu.id}`, { name: bu.name, description: bu.description || "", owner_id: bu.owner_id, logo: null });
                      toast.success("Logo removed");
                      load();
                    } catch (err) { toast.error(formatApiError(err)); }
                  }}
                  className="p-1.5 border border-zinc-300 rounded-sm hover:bg-red-50 hover:border-red-300 text-zinc-600 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        ) : null}
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Business Unit</div>
          <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {bu.name}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{bu.description}</p>
          <div className="text-xs text-zinc-500 mt-2">Owner: <b>{uname(bu.owner_id)}</b></div>
        </div>
        {canEdit && !bu.logo && (
          <label className="text-xs border border-zinc-300 px-3 py-1.5 rounded-sm hover:bg-zinc-50 cursor-pointer flex items-center gap-1" data-testid="bu-logo-upload-label">
            <Upload className="w-3.5 h-3.5" /> Upload logo
            <input type="file" accept="image/*" className="hidden" data-testid="bu-logo-upload-input" onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 1024 * 1024) return toast.error("Logo must be under 1 MB");
              const reader = new FileReader();
              reader.onload = async () => {
                try {
                  await api.put(`/business-units/${bu.id}`, { name: bu.name, description: bu.description || "", owner_id: bu.owner_id, logo: reader.result });
                  toast.success("Logo updated");
                  load();
                } catch (err) { toast.error(formatApiError(err)); }
              };
              reader.readAsDataURL(f);
            }} />
          </label>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 mb-6 flex gap-6 flex-wrap">
        {visibleTabs.map((t) => (
          <button key={t.id} data-testid={t.testId} onClick={() => setTab(t.id)}
            className={`pb-3 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-zinc-900 text-zinc-900 font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Departments tab */}
      {tab === "departments" && (
        <div>
          <div className="flex justify-end mb-4">
            {canEdit && (
              <button data-testid="dept-new-btn" onClick={() => setEditingDept({ name: "", description: "", manager_id: null })} className="bg-zinc-900 text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-zinc-800 flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Position Profile
              </button>
            )}
          </div>
          <div className="border border-zinc-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-5 py-3">Position Profile</th>
                  <th className="text-left px-5 py-3">BU Manager</th>
                  <th className="text-left px-5 py-3">Templates</th>
                  <th className="text-left px-5 py-3">Policies</th>
                  {canEdit && <th className="px-5 py-3 w-32"></th>}
                </tr>
              </thead>
              <tbody>
                {depts.map((d) => {
                  const deptPolicies = policies.filter((p) => p.department_id === d.id);
                  return (
                  <tr key={d.id} className="border-t border-zinc-200 align-top">
                    <td className="px-5 py-3">
                      <div className="font-medium">{d.name}</div>
                      <div className="text-xs text-zinc-500">{d.description}</div>
                    </td>
                    <td className="px-5 py-3">{uname(d.manager_id)}</td>
                    <td className="px-5 py-3 tabular-nums">{templates.filter((t) => t.department_id === d.id).length}</td>
                    <td className="px-5 py-3">
                      <PoliciesCell
                        deptId={d.id}
                        policies={deptPolicies}
                        canEdit={canEdit}
                        onChanged={load}
                      />
                    </td>
                    {canEdit && (
                      <td className="px-5 py-3 text-right">
                        <button data-testid={`dept-edit-${d.id}`} onClick={() => setEditingDept(d)} className="text-zinc-600 hover:text-zinc-900 mr-3"><Pencil className="w-4 h-4 inline" /></button>
                        <button data-testid={`dept-delete-${d.id}`} onClick={() => delDept(d.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4 inline" /></button>
                      </td>
                    )}
                  </tr>
                );
                })}
                {depts.length === 0 && <tr><td colSpan={canEdit ? 5 : 4} className="px-5 py-10 text-center text-zinc-500">No Position Profiles yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Templates tab */}
      {tab === "templates" && (
        <div>
          {/* Templates sub-tabs: KPA Templates / KPA Policies / SOPs */}
          <div className="border-b border-zinc-200 mb-4 flex gap-6">
            {[
              { id: "kpa", label: `KPA Templates (${templates.length})`, testId: "tpl-subtab-kpa" },
              { id: "policies", label: `KPA Policies (${policies.filter((p) => (p.doc_type || "policy") === "policy").length})`, testId: "tpl-subtab-policies" },
              { id: "sops", label: `SOPs (${policies.filter((p) => p.doc_type === "sop").length})`, testId: "tpl-subtab-sops" },
            ].map((t) => (
              <button
                key={t.id} data-testid={t.testId}
                onClick={() => setTemplatesSubTab(t.id)}
                className={`pb-3 text-sm border-b-2 -mb-px transition-colors ${
                  templatesSubTab === t.id ? "border-zinc-900 text-zinc-900 font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"
                }`}>
                {t.label}
              </button>
            ))}
          </div>
          {templatesSubTab === "policies" && (
            <PolicyLibrary
              buId={id}
              docType="policy"
              policies={policies.filter((p) => (p.doc_type || "policy") === "policy")}
              depts={depts}
              users={users}
              canEdit={canTemplate}
              canApprove={canEdit}
              onChanged={load}
            />
          )}
          {templatesSubTab === "sops" && (
            <PolicyLibrary
              buId={id}
              docType="sop"
              policies={policies.filter((p) => p.doc_type === "sop")}
              depts={depts}
              users={users}
              canEdit={canTemplate}
              canApprove={canEdit}
              onChanged={load}
            />
          )}
          {templatesSubTab === "kpa" && (
        <div>
          <div className="flex justify-between items-end mb-4 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Library</div>
              <div className="text-xs text-zinc-500">Use a starter to jump-start a new KPA template.</div>
            </div>
            {canTemplate && depts.length > 0 && (
              <div className="flex gap-2">
                <button data-testid={TEMPLATE.importBtn} onClick={() => setImporting(true)} className="border border-zinc-300 px-4 py-2 rounded-sm text-sm font-medium hover:bg-zinc-50 flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Import from Excel
                </button>
                <button data-testid={TEMPLATE.newBtn} onClick={() => setCreatingTpl(true)} style={{ background: "var(--brand-primary)" }} className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2">
                  <Plus className="w-4 h-4" /> New template
                </button>
              </div>
            )}
          </div>

          {/* Library / starter templates */}
          {canTemplate && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <StarterCard
                title="Blank KPA Template"
                subtitle="Start from scratch — no rows."
                tone="zinc"
                disabled={depts.length === 0}
                onUse={() => setBlankSeed({ rows: [], name: "New KPA Template" })}
                testId="library-blank"
              />
              <StarterCard
                title="Standard 4-Perspective"
                subtitle="Financial · Customer · Process · Business Principles — 4 empty rows."
                tone="blue"
                disabled={depts.length === 0}
                onUse={() => setBlankSeed({
                  rows: [
                    { perspective: "Financial Impact", strategic_goal: "", kpa: "", weight: 25, measurement_tool: "", comments: "" },
                    { perspective: "Customer Satisfaction Impact", strategic_goal: "", kpa: "", weight: 25, measurement_tool: "", comments: "" },
                    { perspective: "Internal Process Impact", strategic_goal: "", kpa: "", weight: 25, measurement_tool: "", comments: "" },
                    { perspective: "Business Principles", strategic_goal: "", kpa: "", weight: 25, measurement_tool: "", comments: "" },
                  ],
                  name: "Standard KPA Template",
                })}
                testId="library-standard"
              />
            </div>
          )}

          {/* Existing templates */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Your templates</div>
            {canTemplate && templates.length > 0 && (
              <div className="flex items-center gap-3">
                <button
                  data-testid="tpl-select-all"
                  onClick={() => {
                    const selectable = templates.filter((t) => t.status !== "approved").map((t) => t.id);
                    setSelectedTpls((s) => s.size === selectable.length ? new Set() : new Set(selectable));
                  }}
                  className="text-[10px] uppercase tracking-wider text-zinc-600 hover:text-zinc-900"
                >
                  {selectedTpls.size > 0 ? "Clear" : "Select all"}
                </button>
                {selectedTpls.size > 0 && (
                  <button
                    data-testid="tpl-bulk-delete"
                    onClick={async () => {
                      const ids = [...selectedTpls];
                      if (!confirm(`Delete ${ids.length} template${ids.length === 1 ? "" : "s"}? Reviews based on them will also be removed.`)) return;
                      try {
                        const { data } = await api.post("/templates/bulk-delete", { ids });
                        toast.success(`Deleted ${data.deleted?.templates || 0} templates · ${data.deleted?.reviews || 0} reviews cleaned`);
                        if (data.skipped_locked?.length) toast.warning(`${data.skipped_locked.length} locked template(s) were skipped`);
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="bg-red-600 text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-red-700 flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete {selectedTpls.size} selected
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => {
              const [statusLabel, statusCls] = STATUS_BADGE[t.status] || ["—", ""];
              const locked = t.status === "approved";
              const checked = selectedTpls.has(t.id);
              return (
                <div key={t.id} className={`border bg-white p-5 transition-colors ${checked ? "border-blue-500 ring-1 ring-blue-200" : "border-zinc-200 hover:border-zinc-400"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      {canTemplate && !locked && (
                        <input
                          type="checkbox"
                          data-testid={`tpl-select-${t.id}`}
                          checked={checked}
                          onChange={() => setSelectedTpls((s) => {
                            const n = new Set(s);
                            if (n.has(t.id)) n.delete(t.id); else n.add(t.id);
                            return n;
                          })}
                          className="cursor-pointer"
                          title="Select for bulk delete"
                        />
                      )}
                      <FileSpreadsheet className="w-5 h-5 text-zinc-400" strokeWidth={1.5} />
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${statusCls}`}>
                      {locked ? <><Lock className="w-3 h-3 inline mr-1" /> {statusLabel}</> : statusLabel}
                    </span>
                  </div>
                  <div className="mt-3 font-semibold">{t.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{dname(t.department_id)} · {t.fiscal_year}</div>
                  <div className="text-xs text-zinc-500 mt-1">{t.rows?.length || 0} KPA rows</div>
                  <div className="mt-4 flex gap-2">
                    <Link to={`/templates/${t.id}/view`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-zinc-300 rounded-sm py-1.5 hover:bg-zinc-50" data-testid={`template-view-${t.id}`}>View</Link>
                    {!locked && canTemplate && (
                      <Link to={`/templates/${t.id}/edit`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs border border-zinc-300 rounded-sm py-1.5 hover:bg-zinc-50" data-testid={`template-edit-${t.id}`}>Edit</Link>
                    )}
                    {!locked && canEdit && (
                      <button
                        data-testid={`template-delete-${t.id}`}
                        title="Delete template"
                        onClick={async () => {
                          if (!window.confirm(`Delete template "${t.name}"? Any reviews created from it will also be removed.`)) return;
                          try {
                            const { data } = await api.delete(`/templates/${t.id}`);
                            toast.success(`Template deleted${data?.deleted?.reviews ? ` · ${data.deleted.reviews} review(s) cleaned` : ""}`);
                            load();
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="inline-flex items-center justify-center border border-red-200 text-red-600 rounded-sm py-1.5 px-2 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {templates.length === 0 && (
              <div className="col-span-full text-center text-zinc-500 text-sm py-10 border border-dashed border-zinc-300">
                No templates yet. Use a library starter, click Import or New template to get started.
              </div>
            )}
          </div>
        </div>
          )}
        </div>
      )}

      {/* Dashboard tab — BU-scoped KPIs */}
      {tab === "dashboard" && (
        <BUDashboard buId={id} canView={canTemplate || canEdit} onNavTab={setTab} />
      )}

      {/* Reports tab — BU-scoped analytics + Excel export */}
      {tab === "reports" && (
        <Reports initialScope={id} lockScope />
      )}

      {/* Employees tab — tile grid (or a plain user's own profile inline). */}
      {tab === "employees" && (
        isPlainUser
          ? <EmployeeDetail userId={user.id} buId={id} canManage={false} inline
                            onClose={() => {}} onChanged={() => {}} />
          : <BUEmployees buId={id} canManage={canTemplate}
                         restrictToManagerId={isManager ? user.id : null} />
      )}

      {/* BU Skills Matrix tab */}
      {tab === "skills" && (
        <BUSkillsMatrix buId={id} />
      )}

      {/* Organogram tab — org tree for this BU */}
      {tab === "organogram" && (
        <BUOrganogram buId={id} />
      )}

      {/* Performance Improvement Plans tab */}
      {tab === "pip" && (
        <BUPIPs buId={id} canCreate={canTemplate} canApprove={canEdit} />
      )}

      {/* What-if Scenarios tab */}
      {tab === "whatif" && (
        <BUScenarios buId={id} canPublish={canEdit} />
      )}

      {/* My Policies (this user's own signature queue) */}
      {tab === "mine" && <MyPoliciesLazy />}

      {/* Inbox tab (BU activity feed) */}
      {tab === "inbox" && (
        <BUInbox notifications={notifs} templates={templates} reviews={reviews} onChange={load} />
      )}

      {/* My Activity tab — the current user's audit trail scoped to their own actions */}
      {tab === "my_activity" && (
        <MyActivity buId={id} userId={user?.id} />
      )}

      {/* Users tab */}
      {tab === "users" && (
        <BUUsers buId={id} allUsers={users} canApprove={canEdit} onChange={load}
                 restrictToManagerId={isManager ? user.id : null} />
      )}

      {/* Reviews tab */}
      {tab === "reviews" && (
        <div>
          <div className="flex justify-between items-end mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Quarterly reviews</div>
              <div className="text-xs text-zinc-500">Start a review against any approved KPA template.</div>
            </div>
            {canEdit && (
              <button
                data-testid="review-new-btn"
                onClick={() => setCreatingReview(true)}
                style={{ background: "var(--brand-primary)" }}
                className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Start review
              </button>
            )}
          </div>

          {/* Filters */}
          {reviews.length > 0 && (() => {
            const reviewedUserIds = Array.from(new Set(reviews.flatMap((r) => [r.employee_id, r.manager_id]).filter(Boolean)));
            return (
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Filters</div>
                <select
                  data-testid="review-user-filter"
                  value={reviewUserFilter}
                  onChange={(e) => setReviewUserFilter(e.target.value)}
                  className="border border-zinc-300 px-3 py-1.5 rounded-sm text-xs bg-white"
                >
                  <option value="all">All users</option>
                  {reviewedUserIds.map((uid) => (
                    <option key={uid} value={uid}>{uname(uid)}</option>
                  ))}
                </select>
                <div className="flex gap-1">
                  {["All", "Q1", "Q2", "Q3", "Q4"].map((q) => {
                    const active = reviewQuarterFilter === q;
                    return (
                      <button
                        key={q}
                        data-testid={`review-quarter-filter-${q}`}
                        onClick={() => setReviewQuarterFilter(q)}
                        style={active ? { background: "var(--brand-primary)", color: "white", borderColor: "var(--brand-primary)" } : undefined}
                        className={`px-3 py-1.5 text-xs rounded-sm border ${active ? "" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50 bg-white"}`}
                      >
                        {q}
                      </button>
                    );
                  })}
                </div>
                {(reviewUserFilter !== "all" || reviewQuarterFilter !== "All") && (
                  <button
                    data-testid="review-filter-clear"
                    onClick={() => { setReviewUserFilter("all"); setReviewQuarterFilter("All"); }}
                    className="text-xs text-zinc-500 hover:text-zinc-900 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            );
          })()}

          <div className="border border-zinc-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-5 py-3">Template</th>
                  <th className="text-left px-5 py-3">Employee</th>
                  <th className="text-left px-5 py-3">Quarter</th>
                  <th className="text-left px-5 py-3">Stage</th>
                  <th className="text-right px-5 py-3">Final</th>
                  <th className="text-right px-5 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filtered = reviews.filter((r) => {
                    if (reviewUserFilter !== "all" && r.employee_id !== reviewUserFilter && r.manager_id !== reviewUserFilter) return false;
                    if (reviewQuarterFilter !== "All" && r.quarter !== reviewQuarterFilter) return false;
                    return true;
                  });
                  if (filtered.length === 0) {
                    return (
                      <tr><td colSpan={6} className="px-5 py-16 text-center text-zinc-500">
                        <ClipboardCheck className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                        {reviews.length === 0 ? "No reviews in this BU yet." : "No reviews match the current filter."}
                      </td></tr>
                    );
                  }
                  return filtered.map((r) => {
                    const [label, cls] = STAGE_BADGE[r.stage] || [r.stage, ""];
                    const isSuper = user?.role === "super_admin";
                    return (
                      <tr key={r.id} data-testid={`review-row-${r.id}`} className="border-t border-zinc-200">
                        <td className="px-5 py-3"><Link className="font-medium hover:underline" to={`/reviews/${r.id}`}>{r.template_snapshot?.name}</Link></td>
                        <td className="px-5 py-3">{uname(r.employee_id)}</td>
                        <td className="px-5 py-3">{r.quarter || "—"} · <span className="text-zinc-500">{r.fiscal_year}</span></td>
                        <td className="px-5 py-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm ${cls}`}>{label}</span></td>
                        <td className="px-5 py-3 text-right tabular-nums font-semibold">{r.final_total ?? "—"}</td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <Link
                            to={`/reviews/${r.id}`}
                            data-testid={`review-view-${r.id}`}
                            className="text-xs border border-zinc-300 px-3 py-1 rounded-sm hover:bg-zinc-50 text-zinc-700 mr-2 inline-block"
                          >
                            View
                          </Link>
                          {isSuper && (
                            <button
                              data-testid={`review-delete-${r.id}`}
                              title="Delete review (Super Admin only)"
                              onClick={async () => {
                                if (!confirm(`Delete this ${r.quarter || ""} review for ${uname(r.employee_id)}? This cannot be undone.`)) return;
                                try {
                                  await api.delete(`/reviews/${r.id}`);
                                  toast.success("Review deleted");
                                  load();
                                } catch (err) { toast.error(formatApiError(err)); }
                              }}
                              className="text-red-600 hover:text-red-800 p-1.5 inline-flex items-center justify-center"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {editingDept && (
        <Modal title={editingDept.id ? "Edit Position Profile" : "New Position Profile"} onClose={() => setEditingDept(null)}>
          <DeptForm initial={editingDept} users={users.filter((u) => u.role === "bu_manager" || u.role === "super_admin")} onCancel={() => setEditingDept(null)} onSave={saveDept} />
        </Modal>
      )}
      {creatingTpl && (
        <Modal title="New KPA template" onClose={() => setCreatingTpl(false)}>
          <NewTemplateForm depts={depts} buId={id} onCancel={() => setCreatingTpl(false)} onCreated={(tid) => { setCreatingTpl(false); nav(`/templates/${tid}/edit`); }} />
        </Modal>
      )}
      {importing && (
        <Modal title="Import KPA template from Excel" onClose={() => setImporting(false)}>
          <ImportForm depts={depts} onCancel={() => setImporting(false)} onImported={(tid) => { setImporting(false); load(); nav(`/templates/${tid}/edit`); }} />
        </Modal>
      )}
      {blankSeed && (
        <Modal title="Create from library" onClose={() => setBlankSeed(null)}>
          <NewTemplateForm depts={depts} buId={id} initialRows={blankSeed.rows} initialName={blankSeed.name} onCancel={() => setBlankSeed(null)} onCreated={(tid) => { setBlankSeed(null); nav(`/templates/${tid}/edit`); }} />
        </Modal>
      )}
      {creatingReview && (
        <Modal title="Start a quarterly review" onClose={() => setCreatingReview(false)}>
          <NewReviewForm
            buId={id}
            templates={templates.filter((t) => t.status === "approved")}
            users={users}
            onCancel={() => setCreatingReview(false)}
            onCreated={(rid) => { setCreatingReview(false); load(); nav(`/reviews/${rid}`); }}
          />
        </Modal>
      )}
    </div>
  );
}

function StarterCard({ title, subtitle, tone, disabled, onUse, testId }) {
  const tones = {
    zinc: "border-zinc-300 bg-zinc-50",
    blue: "border-blue-300 bg-blue-50",
  };
  return (
    <div className={`border-2 border-dashed ${tones[tone] || tones.zinc} p-5 flex flex-col`}>
      <FileSpreadsheet className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
      <div className="mt-3 font-semibold">{title}</div>
      <div className="text-xs text-zinc-600 mt-1 flex-1">{subtitle}</div>
      <button data-testid={testId} disabled={disabled} onClick={onUse}
        className="mt-4 w-full text-sm bg-zinc-900 text-white py-1.5 rounded-sm hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed">
        {disabled ? "Add a Position Profile first" : "Use this template"}
      </button>
    </div>
  );
}

function BUInbox({ notifications, templates, reviews, onChange }) {
  const tplIds = new Set(templates.map((t) => t.id));
  const reviewIds = new Set(reviews.map((r) => r.id));
  const items = notifications.filter((n) =>
    (n.template_id && tplIds.has(n.template_id)) ||
    (n.review_id && reviewIds.has(n.review_id))
  );
  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); onChange(); } catch { /* ignore */ }
  };
  return (
    <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
      {items.map((n) => (
        <div key={n.id} className={`px-5 py-4 flex items-start gap-3 ${n.read ? "opacity-60" : ""}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{n.subject}</div>
            <div className="text-sm text-zinc-600 mt-0.5">{n.body}</div>
            {n.review_id && <Link to={`/reviews/${n.review_id}`} className="text-xs text-blue-600 hover:underline mt-1 inline-block mr-3">Open review →</Link>}
            {n.template_id && <Link to={`/templates/${n.template_id}/view`} className="text-xs text-blue-600 hover:underline mt-1 inline-block">Open template →</Link>}
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-2">
              {new Date(n.created_at).toLocaleString()}
            </div>
          </div>
          {!n.read && (
            <button onClick={() => markRead(n.id)} className="text-xs text-zinc-600 hover:text-zinc-900">Mark read</button>
          )}
        </div>
      ))}
      {items.length === 0 && (
        <div className="px-5 py-16 text-center text-zinc-500 text-sm">No inbox messages for this business unit.</div>
      )}
    </div>
  );
}

function DeptForm({ initial, users, onCancel, onSave }) {
  const [v, setV] = useState(initial);
  return (
    <div className="space-y-4">
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Name</label>
        <input data-testid="dept-name-input" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={v.name} onChange={(e) => setV({...v, name: e.target.value})} />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Description</label>
        <input className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={v.description || ""} onChange={(e) => setV({...v, description: e.target.value})} />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">BU Manager</label>
        <select className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={v.manager_id || ""} onChange={(e) => setV({...v, manager_id: e.target.value || null})}>
          <option value="">— Unassigned —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
        <button data-testid="dept-save-btn" onClick={() => onSave(v)} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800">Save</button>
      </div>
    </div>
  );
}

function NewTemplateForm({ depts, buId, initialRows, initialName, onCancel, onCreated }) {
  const [v, setV] = useState({ name: initialName || "", department_id: depts[0]?.id || "", fiscal_year: "FY2026" });
  const submit = async () => {
    if (!v.name) return toast.error("Enter a template name");
    try {
      const { data } = await api.post("/templates", { ...v, business_unit_id: buId, rows: initialRows || [] });
      toast.success("Created");
      onCreated(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Template name</label>
        <input data-testid="template-name-input" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={v.name} onChange={(e) => setV({...v, name: e.target.value})} />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Position Profile</label>
        <select data-testid="template-dept-select" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={v.department_id} onChange={(e) => setV({...v, department_id: e.target.value})}>
          {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <FiscalYearSelect value={v.fiscal_year} onChange={(fy) => setV({...v, fiscal_year: fy})} />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
        <button data-testid="template-create-btn" onClick={submit} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800">Create & edit</button>
      </div>
    </div>
  );
}

function ImportForm({ depts, onCancel, onImported }) {
  const [name, setName] = useState("");
  const [departmentId, setDepartmentId] = useState(depts[0]?.id || "");
  const [fiscalYear, setFiscalYear] = useState("FY2026");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choose a .xlsx file");
    if (!name) return toast.error("Enter a name for the template");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      fd.append("department_id", departmentId);
      fd.append("fiscal_year", fiscalYear);
      const { data } = await api.post("/templates/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.rows?.length || 0} KPA rows`);
      onImported(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
        We will look for columns: <b>Perspective</b>, <b>KPA Strategic Goal</b>, <b>KPA</b>, <b>Perspective KPA Weight</b>, <b>Measurement Tool</b>, <b>Comments</b> — header names are case-insensitive and &ldquo;Weighting/Weight %&rdquo; are also accepted.
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Template name</label>
        <input data-testid="import-name-input" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Finance KPA Template" />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Position Profile</label>
        <select data-testid="import-dept-select" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
          {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </div>
      <FiscalYearSelect value={fiscalYear} onChange={setFiscalYear} />
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Excel file (.xlsx)</label>
        <input data-testid="import-file-input" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block mt-1 text-sm" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
        <button data-testid="import-submit-btn" disabled={busy} onClick={submit} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">{busy ? "Importing…" : "Import"}</button>
      </div>
    </div>
  );
}

export function FiscalYearSelect({ value, onChange }) {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current - 1; y <= current + 7; y++) years.push(`FY${y}`);
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Fiscal year</label>
      <select data-testid="template-fy-select" className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={value} onChange={(e) => onChange(e.target.value)}>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-sm max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">{title}</div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function BUUsers({ buId, allUsers, canApprove, onChange, restrictToManagerId = null }) {
  const inBU = allUsers.filter((u) => u.business_unit_id === buId);
  const users = restrictToManagerId
    ? inBU.filter((u) => u.line_manager_id === restrictToManagerId)
    : inBU;
  const pending = users.filter((u) => u.status === "pending_approval");
  const active = users.filter((u) => u.status !== "pending_approval" && u.status !== "denied");
  const denied = users.filter((u) => u.status === "denied");

  const approve = async (u) => {
    if (!confirm(`Approve ${u.name} ${u.surname || ""} (${u.email})? They will be able to sign in immediately.`)) return;
    try { await api.post(`/users/${u.id}/approve`); toast.success("Approved"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deny = async (u) => {
    if (!confirm(`Deny ${u.email}? They won't be able to sign in.`)) return;
    try { await api.post(`/users/${u.id}/deny`); toast.success("Denied"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const statusBadge = (s) => {
    const c = {
      active: "bg-emerald-50 text-emerald-800 border-emerald-200",
      pending_approval: "bg-amber-50 text-amber-800 border-amber-200",
      denied: "bg-red-50 text-red-800 border-red-200",
    }[s || "active"] || "bg-zinc-50 text-zinc-700 border-zinc-200";
    const label = (s || "active").replace("_", " ");
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${c}`}>{label}</span>;
  };

  const Row = ({ u, showActions }) => (
    <tr key={u.id} className="border-t border-zinc-200">
      <td className="px-5 py-3">
        <div className="font-medium">{u.name} {u.surname || ""}</div>
        <div className="text-xs text-zinc-500">{u.title || ""}</div>
      </td>
      <td className="px-5 py-3 text-zinc-600">{u.email}</td>
      <td className="px-5 py-3 text-zinc-600">{u.phone_number || "—"}</td>
      <td className="px-5 py-3">{statusBadge(u.status)}</td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {showActions && canApprove && (
          <>
            <button data-testid={`bu-user-approve-${u.id}`} onClick={() => approve(u)} className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-sm hover:bg-zinc-800 mr-2">Approve</button>
            <button data-testid={`bu-user-deny-${u.id}`} onClick={() => deny(u)} className="text-xs border border-zinc-300 px-3 py-1.5 rounded-sm hover:bg-zinc-50">Deny</button>
          </>
        )}
      </td>
    </tr>
  );

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Pending approval ({pending.length})</div>
        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500"><tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Phone</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="px-5 py-3 w-48"></th>
            </tr></thead>
            <tbody>
              {pending.map((u) => <Row key={u.id} u={u} showActions />)}
              {pending.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-500 text-sm">No pending registrations.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Active ({active.length})</div>
        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500"><tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Phone</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="px-5 py-3 w-48"></th>
            </tr></thead>
            <tbody>
              {active.map((u) => <Row key={u.id} u={u} showActions={false} />)}
              {active.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-zinc-500 text-sm">No active users yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {denied.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Denied ({denied.length})</div>
          <div className="border border-zinc-200 bg-white">
            <table className="w-full text-sm">
              <tbody>
                {denied.map((u) => <Row key={u.id} u={u} showActions={canApprove} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function NewReviewForm({ buId, templates, users, onCancel, onCreated }) {
  const [templateId, setTemplateId] = useState(templates[0]?.id || "");
  const [employeeId, setEmployeeId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [quarter, setQuarter] = useState("Q1");
  const [busy, setBusy] = useState(false);

  // Scope candidate employees + managers to users that belong to this BU.
  const buUsers = users.filter((u) => u.business_unit_id === buId && u.status !== "denied" && u.status !== "pending_approval");
  const managerCandidates = buUsers.filter((u) => u.role === "bu_manager" || u.role === "bu_owner" || u.role === "super_admin");

  const tpl = templates.find((t) => t.id === templateId);

  const submit = async () => {
    if (!templateId) return toast.error("Pick an approved template");
    if (!employeeId) return toast.error("Pick an employee");
    if (!managerId) return toast.error("Pick the reviewing manager");
    if (employeeId === managerId) return toast.error("Employee and manager cannot be the same person");
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", {
        template_id: templateId,
        employee_id: employeeId,
        manager_id: managerId,
        quarter,
      });
      toast.success(`${quarter} review started`);
      onCreated(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (templates.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-zinc-700 bg-amber-50 border border-amber-200 p-4 rounded-sm">
          <b>No approved templates in this BU yet.</b><br />
          A KPA template must complete the 3-step approval workflow (Manager → BU Owner → Super Admin sign-off) before reviews can be started from it.
        </div>
        <div className="flex justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Approved template</label>
        <select
          data-testid="review-template-select"
          className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
          value={templateId} onChange={(e) => setTemplateId(e.target.value)}
        >
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.fiscal_year}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Employee</label>
          <select
            data-testid="review-employee-select"
            className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
            value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
          >
            <option value="">— Select employee —</option>
            {buUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Reviewing manager</label>
          <select
            data-testid="review-manager-select"
            className="w-full mt-1 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white"
            value={managerId} onChange={(e) => setManagerId(e.target.value)}
          >
            <option value="">— Select manager —</option>
            {managerCandidates.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Quarter</label>
        <div className="flex gap-2 mt-1">
          {["Q1", "Q2", "Q3", "Q4"].map((q) => (
            <button
              key={q}
              type="button"
              data-testid={`review-quarter-${q}`}
              onClick={() => setQuarter(q)}
              style={quarter === q ? { background: "var(--brand-primary)", color: "white", borderColor: "var(--brand-primary)" } : undefined}
              className={`px-4 py-1.5 text-sm rounded-sm border ${quarter === q ? "" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}
            >
              {q}
            </button>
          ))}
        </div>
        {tpl && <div className="text-xs text-zinc-500 mt-2">Will create the <b>{quarter} {tpl.fiscal_year}</b> review for the selected employee.</div>}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
        <button
          data-testid="review-create-btn"
          disabled={busy}
          onClick={submit}
          style={{ background: "var(--brand-primary)" }}
          className="px-4 py-2 text-sm text-white rounded-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Starting…" : "Start review"}
        </button>
      </div>
    </div>
  );
}


function PolicyLibrary({ buId, docType, policies, depts, users, canEdit, canApprove, onChanged }) {
  const nav = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [signoff, setSignoff] = useState({}); // { [policyId]: {total, signed} }

  const label = docType === "sop" ? "SOP" : "Policy";
  const labelPlural = docType === "sop" ? "SOPs" : "Policies";

  // Only "created by me" or my BU users appear in the signer picker to avoid crossing BU boundaries
  const buUsers = users.filter((u) => u.business_unit_id === buId);

  const loadSignoff = async () => {
    // Only load sign-off for approved docs — drafts have nothing to show
    const approved = policies.filter((p) => (p.status || "approved") === "approved");
    const entries = await Promise.all(approved.map(async (p) => {
      try {
        const { data } = await api.get(`/policies/${p.id}/signoff-status`);
        return [p.id, { total: data.total, signed: data.signed }];
      } catch { return [p.id, null]; }
    }));
    const map = {};
    entries.forEach(([id, v]) => { if (v) map[id] = v; });
    setSignoff(map);
  };
  useEffect(() => { loadSignoff(); /* eslint-disable-next-line */ }, [policies.map((p) => p.id + p.status).join(",")]);

  const submit = async (pid) => {
    try { await api.post(`/policies/${pid}/submit`); toast.success(`${label} submitted for approval`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const approve = async (pid) => {
    try {
      const { data } = await api.post(`/policies/${pid}/approve`);
      toast.success(`${label} approved — ${data.signers_notified} signer${data.signers_notified === 1 ? "" : "s"} notified`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (p) => {
    if (!confirm(`Delete ${label} "${p.name}"?`)) return;
    try { await api.delete(`/policies/${p.id}`); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [showUpload, setShowUpload] = useState(false);

  const statusBadge = (s) => {
    const badges = {
      draft: ["Draft", "bg-zinc-50 text-zinc-700 border-zinc-200"],
      pending_owner: ["Pending Owner Approval", "bg-amber-50 text-amber-800 border-amber-200"],
      approved: ["Approved & Sent", "bg-emerald-50 text-emerald-800 border-emerald-200"],
    };
    const [text, cls] = badges[s || "approved"] || badges.approved;
    return <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${cls}`}>{text}</span>;
  };

  const scopeDesc = (p) => {
    if (p.signer_scope === "bu") return `Everyone in this BU`;
    if (p.signer_scope === "users") return `${(p.signer_user_ids || []).length} specific user${(p.signer_user_ids || []).length === 1 ? "" : "s"}`;
    const names = (p.signer_profile_ids || []).map((id) => depts.find((d) => d.id === id)?.name).filter(Boolean);
    return names.length ? names.join(", ") : "—";
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{labelPlural} library</div>
          <div className="text-xs text-zinc-500">
            Upload {docType === "sop" ? "Standard Operating Procedures" : "policies"}, drop signature slots, then submit for BU Owner approval. Approved documents are automatically sent to the assigned signers.
          </div>
        </div>
        {canEdit && (
          <button
            data-testid={`${docType}-upload-btn`}
            onClick={() => setShowUpload(true)}
            style={{ background: "var(--brand-primary)" }}
            className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Upload {label}
          </button>
        )}
      </div>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Signer scope</th>
              <th className="text-left px-4 py-3">Sign-off</th>
              <th className="text-left px-4 py-3">Uploaded by</th>
              <th className="px-4 py-3 w-56"></th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => {
              const s = signoff[p.id];
              const status = p.status || "approved";
              return (
                <tr key={p.id} className="border-t border-zinc-200">
                  <td className="px-4 py-3">
                    <Link to={`/policies/${p.id}`} className="hover:underline font-medium" data-testid={`policy-link-${p.id}`}>
                      {p.name}
                    </Link>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{(p.slots || []).length} slot{(p.slots || []).length === 1 ? "" : "s"}</div>
                  </td>
                  <td className="px-4 py-3">{statusBadge(status)}</td>
                  <td className="px-4 py-3 text-zinc-700">{scopeDesc(p)}</td>
                  <td className="px-4 py-3 tabular-nums">
                    {status === "approved" && s ? (
                      <span className={s.signed === s.total ? "text-emerald-700" : "text-amber-700"}>
                        {s.signed} / {s.total}
                      </span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{p.created_by_name || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {status === "draft" && canEdit && (
                      <button
                        data-testid={`policy-submit-${p.id}`}
                        onClick={() => submit(p.id)}
                        className="text-xs bg-amber-600 text-white px-3 py-1 rounded-sm hover:bg-amber-700 mr-2"
                        title={(p.slots || []).length === 0 ? "Add signature slots first" : "Submit for BU Owner approval"}
                      >
                        Submit for approval
                      </button>
                    )}
                    {status === "pending_owner" && canApprove && (
                      <button
                        data-testid={`policy-approve-${p.id}`}
                        onClick={() => approve(p.id)}
                        className="text-xs bg-emerald-700 text-white px-3 py-1 rounded-sm hover:bg-emerald-800 mr-2"
                      >
                        Approve & send
                      </button>
                    )}
                    <button
                      onClick={() => nav(`/policies/${p.id}`)}
                      className="text-xs text-blue-600 hover:underline mr-3"
                    >
                      Open
                    </button>
                    {canEdit && (
                      <button
                        data-testid={`policy-delete-${p.id}`}
                        onClick={() => del(p)}
                        className="text-red-600 hover:text-red-800"
                        title={`Delete ${label}`}
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {policies.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No {labelPlural.toLowerCase()} uploaded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <PolicyUploadModal
          buId={buId}
          docType={docType}
          depts={depts}
          buUsers={buUsers}
          uploading={uploading}
          setUploading={setUploading}
          onClose={() => setShowUpload(false)}
          onCreated={(newId) => {
            setShowUpload(false);
            onChanged();
            // Send admin straight into the editor to drop signature slots
            nav(`/policies/${newId}`);
          }}
        />
      )}
    </div>
  );
}


function PolicyUploadModal({ buId, docType, depts, buUsers, uploading, setUploading, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [scope, setScope] = useState("bu");
  const [selectedProfiles, setSelectedProfiles] = useState(() => new Set());
  const [selectedUsers, setSelectedUsers] = useState(() => new Set());

  const label = docType === "sop" ? "SOP" : "Policy";
  const buDepts = depts.filter((d) => d.business_unit_id === buId);

  const toggle = (setter) => (id) => setter((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const submit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (!file) return toast.error("Attach a .docx or .pdf file");
    if (file.size > 6 * 1024 * 1024) return toast.error("File must be under 6 MB");
    if (scope === "profiles" && selectedProfiles.size === 0) return toast.error("Pick at least one Position Profile");
    if (scope === "users" && selectedUsers.size === 0) return toast.error("Pick at least one user");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name.trim());
      fd.append("business_unit_id", buId);
      fd.append("doc_type", docType);
      fd.append("signer_scope", scope);
      if (scope === "profiles") fd.append("signer_profile_ids", [...selectedProfiles].join(","));
      if (scope === "users") fd.append("signer_user_ids", [...selectedUsers].join(","));
      const { data } = await api.post("/policies", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded — drop signature slots next`);
      onCreated(data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid={`${docType}-upload-modal`}>
      <div className="bg-white border border-zinc-200 rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">Upload {label}</div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Name</div>
            <input
              data-testid={`${docType}-name-input`}
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder={docType === "sop" ? "e.g. Change Management SOP" : "e.g. Anti-Bribery Policy"}
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">File (.docx or .pdf, ≤ 6 MB)</div>
            <input
              data-testid={`${docType}-file-input`}
              type="file" accept=".docx,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Who has to sign this {label}?</div>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="scope" value="bu" checked={scope === "bu"} onChange={() => setScope("bu")} data-testid={`${docType}-scope-bu`} />
                Everyone in this BU
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="scope" value="profiles" checked={scope === "profiles"} onChange={() => setScope("profiles")} data-testid={`${docType}-scope-profiles`} />
                Specific Position Profiles
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="scope" value="users" checked={scope === "users"} onChange={() => setScope("users")} data-testid={`${docType}-scope-users`} />
                Individual users
              </label>
            </div>
          </div>
          {scope === "profiles" && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Select Position Profiles</div>
              <div className="border border-zinc-200 rounded-sm max-h-48 overflow-y-auto divide-y divide-zinc-100">
                {buDepts.length === 0 && <div className="text-xs text-zinc-500 p-3">No Position Profiles in this BU yet.</div>}
                {buDepts.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer">
                    <input
                      data-testid={`${docType}-profile-${d.id}`}
                      type="checkbox"
                      checked={selectedProfiles.has(d.id)}
                      onChange={() => toggle(setSelectedProfiles)(d.id)}
                    />
                    {d.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {scope === "users" && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Select users</div>
              <div className="border border-zinc-200 rounded-sm max-h-56 overflow-y-auto divide-y divide-zinc-100">
                {buUsers.length === 0 && <div className="text-xs text-zinc-500 p-3">No users in this BU yet.</div>}
                {buUsers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer">
                    <input
                      data-testid={`${docType}-user-${u.id}`}
                      type="checkbox"
                      checked={selectedUsers.has(u.id)}
                      onChange={() => toggle(setSelectedUsers)(u.id)}
                    />
                    <span className="font-medium">{u.name}</span>
                    <span className="text-xs text-zinc-500">{u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-zinc-500">
            After upload, you&apos;ll drop signature slots on the PDF. Once you submit and the BU Owner approves it,
            every selected signer will get a notification to sign.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button
              data-testid={`${docType}-upload-submit`}
              onClick={submit}
              disabled={uploading}
              style={{ background: "var(--brand-primary)" }}
              className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 disabled:opacity-60"
            >
              {uploading ? "Uploading…" : `Upload ${label}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function DashboardStatCard({ label, value, sub, testId, onClick, to, tone = "slate", icon: Icon }) {
  const tones = {
    primary: { border: "border-sky-200 hover:border-sky-600", iconBg: "bg-sky-100 text-sky-700", accent: "text-sky-900" },
    emerald: { border: "border-emerald-200 hover:border-emerald-600", iconBg: "bg-emerald-100 text-emerald-700", accent: "text-emerald-900" },
    violet: { border: "border-violet-200 hover:border-violet-600", iconBg: "bg-violet-100 text-violet-700", accent: "text-violet-900" },
    amber: { border: "border-amber-200 hover:border-amber-600", iconBg: "bg-amber-100 text-amber-800", accent: "text-amber-900" },
    slate: { border: "border-zinc-200 hover:border-zinc-900", iconBg: "bg-zinc-100 text-zinc-700", accent: "text-zinc-900" },
  };
  const t = tones[tone] || tones.slate;
  const base = `group border ${t.border} bg-white p-4 rounded-sm transition-all hover:shadow-md hover:-translate-y-0.5`;
  const body = (
    <>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{label}</div>
        {Icon && (
          <span className={`w-7 h-7 rounded-sm flex items-center justify-center ${t.iconBg}`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        )}
      </div>
      <div className={`text-3xl font-semibold mt-2 tabular-nums ${t.accent}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </>
  );
  if (to) return <Link to={to} data-testid={testId} className={`block cursor-pointer ${base}`}>{body}</Link>;
  if (onClick) return <button onClick={onClick} data-testid={testId} className={`text-left w-full cursor-pointer ${base}`}>{body}</button>;
  return <div data-testid={testId} className={base}>{body}</div>;
}


function BUDashboard({ buId, canView, onNavTab }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/business-units/${buId}/dashboard`); setData(data); }
      catch (e) { setError(formatApiError(e)); }
    })();
  }, [buId]);

  if (!canView) return <div className="text-sm text-zinc-500">You don&apos;t have access to this dashboard.</div>;
  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="bu-dashboard-error">{error}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading dashboard…</div>;

  return (
    <div className="space-y-6" data-testid="bu-dashboard">
      {/* Top-level KPIs — every card is interactive */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DashboardStatCard label="Users" value={data.counts.users} icon={Users} tone="violet"
          onClick={() => onNavTab?.("employees")}
          testId="dashboard-stat-users" />
        <DashboardStatCard label="Position Profiles" value={data.counts.position_profiles} icon={ClipboardCheck} tone="emerald"
          onClick={() => onNavTab?.("departments")}
          testId="dashboard-stat-profiles" />
        <DashboardStatCard label="Templates" value={data.templates.total} icon={FileText} tone="amber"
          sub={Object.entries(data.templates.by_status || {}).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ")}
          onClick={() => onNavTab?.("templates")}
          testId="dashboard-stat-templates" />
        <DashboardStatCard label="Reviews completed" icon={TrendingUp} tone="primary"
          value={`${data.reviews.completed}/${data.reviews.total}`}
          sub={`${data.reviews.total ? Math.round(100 * data.reviews.completed / data.reviews.total) : 0}% overall`}
          to={`/reviews?bu=${buId}`}
          testId="dashboard-stat-reviews" />
      </div>

      {/* KPA quarter breakdown — denominator is user count so we can see BU progress */}
      <div className="border border-zinc-200 bg-white p-5 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">KPA reviews by quarter</div>
          <div className="text-[10px] text-zinc-400">completed / users</div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {["Q1", "Q2", "Q3", "Q4"].map((q, i) => {
            const s = data.reviews.by_quarter[q];
            const usersDenom = data.counts.users || 0;
            const pct = usersDenom ? Math.round(100 * s.completed / usersDenom) : 0;
            const tones = ["from-sky-50 to-white border-sky-200", "from-emerald-50 to-white border-emerald-200",
                           "from-violet-50 to-white border-violet-200", "from-amber-50 to-white border-amber-200"];
            return (
              <Link key={q} to={`/reviews?bu=${buId}&quarter=${q}`}
                data-testid={`dashboard-quarter-${q}`}
                className={`border rounded-sm p-3 transition-all hover:shadow-md hover:-translate-y-0.5 block bg-gradient-to-b ${tones[i]}`}>
                <div className="text-xs font-semibold text-zinc-700">{q}</div>
                <div className="text-2xl font-semibold tabular-nums mt-1 text-zinc-900">{s.completed}<span className="text-sm text-zinc-400">/{usersDenom}</span></div>
                <div className="text-[11px] text-zinc-500">{pct}% of users done</div>
                <div className="text-[11px] text-zinc-500">
                  Avg score: <b>{s.avg_score ?? "—"}</b>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Policies + SOPs — click to hop to the Position Profiles tab which houses them */}
      <div className="grid md:grid-cols-2 gap-4">
        {["policies", "sops"].map((key) => {
          const s = data[key];
          const title = key === "policies" ? "Policies" : "SOPs";
          return (
            <button key={key} onClick={() => onNavTab?.("departments")}
              data-testid={`dashboard-card-${key}`}
              className="text-left border border-zinc-200 bg-white p-5 rounded-sm transition-all hover:border-zinc-900 hover:-translate-y-0.5 cursor-pointer">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-3">{title}</div>
              <div className="flex gap-4 text-sm">
                <div><b className="text-2xl tabular-nums">{s.total}</b><div className="text-[10px] uppercase text-zinc-500">Total</div></div>
                <div><b className="text-2xl tabular-nums text-emerald-700">{s.approved}</b><div className="text-[10px] uppercase text-zinc-500">Approved</div></div>
                <div><b className="text-2xl tabular-nums text-amber-700">{s.pending_approval}</b><div className="text-[10px] uppercase text-zinc-500">Pending</div></div>
                <div><b className="text-2xl tabular-nums text-zinc-500">{s.draft}</b><div className="text-[10px] uppercase text-zinc-500">Draft</div></div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Sign-off progress */}
      <div className="border border-zinc-200 bg-white p-5 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Sign-off progress</div>
          <div className="text-xs text-zinc-500 tabular-nums">
            {data.signoff.total_signed} / {data.signoff.total_required} required signatures · {data.signoff.pct}%
          </div>
        </div>
        <div className="w-full h-2 bg-zinc-100 rounded-sm overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${data.signoff.pct}%` }} />
        </div>
        {data.signoff.top_pending.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Documents still awaiting signatures</div>
            <div className="space-y-1.5">
              {data.signoff.top_pending.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm border-t border-zinc-100 pt-1.5">
                  <Link to={`/policies/${p.id}`} className="hover:underline">
                    <span className="text-[10px] uppercase mr-2 text-zinc-400">{p.doc_type}</span>{p.name}
                  </Link>
                  <span className="tabular-nums text-xs text-amber-700">{p.signed}/{p.total} signed</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pending approvals */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-zinc-200 bg-white p-5 rounded-sm">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-3">Templates awaiting approval</div>
          {data.pending_approvals.templates.length === 0 ? (
            <div className="text-sm text-zinc-400">Nothing waiting.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.pending_approvals.templates.map((t) => (
                <li key={t.id} className="text-sm flex items-center justify-between border-t border-zinc-100 pt-1.5 first:border-0 first:pt-0">
                  <span>{t.name}</span>
                  <span className="text-[10px] uppercase text-zinc-500">{(t.status || "").replace("_", " ")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-zinc-200 bg-white p-5 rounded-sm">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-3">Policies/SOPs awaiting approval</div>
          {data.pending_approvals.policies.length === 0 ? (
            <div className="text-sm text-zinc-400">Nothing waiting.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.pending_approvals.policies.map((p) => (
                <li key={p.id} className="text-sm flex items-center justify-between border-t border-zinc-100 pt-1.5 first:border-0 first:pt-0">
                  <Link to={`/policies/${p.id}`} className="hover:underline">
                    <span className="text-[10px] uppercase mr-2 text-zinc-400">{p.doc_type}</span>{p.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="border border-zinc-200 bg-white p-5 rounded-sm">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-3">Recent activity</div>
        {data.recent_activity.length === 0 ? (
          <div className="text-sm text-zinc-400">No recent activity in this BU.</div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {data.recent_activity.map((e) => (
              <li key={e.id} className="py-2 text-sm flex items-start justify-between gap-4">
                <div>
                  <div>{e.summary || e.action}</div>
                  <div className="text-[11px] text-zinc-500">{e.actor_email} · {e.action}</div>
                </div>
                <div className="text-[11px] text-zinc-400 whitespace-nowrap">{(e.created_at || "").split("T")[0]}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function PoliciesCell({ deptId, policies, canEdit, onChanged }) {
  const nav = useNavigate();
  const [uploading, setUploading] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same filename
    if (!f) return;
    if (f.size > 6 * 1024 * 1024) return toast.error("File must be under 6 MB");
    const name = window.prompt("Name for this policy:", f.name.replace(/\.(docx|pdf)$/i, ""));
    if (!name) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("name", name);
      fd.append("department_id", deptId);
      const { data } = await api.post("/policies", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Policy uploaded — drop signature slots on the PDF");
      nav(`/policies/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
  };

  return (
    <div className="space-y-1.5 min-w-[180px]">
      {policies.map((p) => (
        <div key={p.id} className="flex items-center gap-2 text-xs">
          <Link to={`/policies/${p.id}`} className="hover:underline text-zinc-700 truncate flex-1" data-testid={`policy-link-${p.id}`}>
            {p.name}
          </Link>
          <span className="text-[10px] text-zinc-500 tabular-nums whitespace-nowrap">
            {(p.signatures?.length ?? 0)}/{(p.slots?.length ?? 0)}
          </span>
          {canEdit && (
            <button
              data-testid={`policy-delete-${p.id}`}
              onClick={async () => {
                if (!window.confirm(`Delete policy "${p.name}"?`)) return;
                try { await api.delete(`/policies/${p.id}`); toast.success("Policy deleted"); onChanged(); }
                catch (err) { toast.error(formatApiError(err)); }
              }}
              className="text-red-500 hover:text-red-800"
              title="Delete policy"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
      {canEdit && (
        <label className="inline-flex items-center gap-1 text-[11px] border border-dashed border-zinc-300 px-2 py-1 rounded-sm hover:bg-zinc-50 cursor-pointer" data-testid={`policy-upload-${deptId}`}>
          <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload policy"}
          <input type="file" accept=".docx,.pdf" className="hidden" onChange={onPick} disabled={uploading} />
        </label>
      )}
      {!canEdit && policies.length === 0 && <span className="text-xs text-zinc-400">—</span>}
    </div>
  );
}

function EmployeeInitials({ name }) {
  const initials = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 text-lg font-semibold shrink-0">
      {initials || "?"}
    </div>
  );
}


function BUEmployees({ buId, canManage, restrictToManagerId = null }) {
  const [tiles, setTiles] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null); // employee id opened

  const load = async () => {
    try { const { data } = await api.get(`/business-units/${buId}/employees`); setTiles(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [buId]);

  const filtered = tiles.filter((t) => {
    // Managers only see their own direct reports.
    if (restrictToManagerId && t.line_manager_id !== restrictToManagerId) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [t.name, t.email, t.title, t.department_name].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div data-testid="bu-employees">
      <div className="flex items-end justify-between mb-4 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Employees</div>
          <div className="text-xs text-zinc-500">Click any tile to see the employee&apos;s KPA history, documents and Performance Improvement Plans.</div>
        </div>
        <input
          data-testid="employees-search"
          value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees…"
          className="border border-zinc-300 px-3 py-2 rounded-sm text-sm w-72"
        />
      </div>
      {filtered.length === 0 && (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-10 text-center">
          No employees yet in this Business Unit.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((t) => (
          <button
            key={t.id}
            data-testid={`employee-tile-${t.id}`}
            onClick={() => setDetail(t.id)}
            className="text-left border border-zinc-200 bg-white p-4 rounded-sm hover:border-zinc-400 hover:shadow-sm transition"
          >
            <div className="flex items-start gap-3">
              {t.profile_photo_url ? (
                <img src={t.profile_photo_url} alt="" className="w-16 h-16 rounded-full object-cover shrink-0" />
              ) : (
                <EmployeeInitials name={t.name} />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{t.name}</div>
                <div className="text-xs text-zinc-500 truncate">{t.title || "—"}</div>
                <div className="text-[11px] text-zinc-400 truncate">{t.department_name || "—"}</div>
                <div className="text-[11px] text-blue-600 truncate mt-1">{t.email}</div>
                {t.phone && <div className="text-[11px] text-zinc-500">{t.phone}</div>}
                <div className="flex gap-2 mt-2">
                  {t.has_active_pip && <span className="inline-block text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200">PIP</span>}
                  <span className="inline-block text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-zinc-50 text-zinc-600 border border-zinc-200">{t.review_count} reviews</span>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
      {detail && <EmployeeDetail userId={detail} buId={buId} canManage={canManage} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}


function EmployeeDetail({ userId, buId, canManage, onClose, onChanged, inline = false }) {
  const { user: viewer } = useAuth();
  const [h, setH] = useState(null);
  const [showPip, setShowPip] = useState(false);
  const load = async () => {
    try { const { data } = await api.get(`/users/${userId}/history`); setH(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  if (!h) return null;
  const u = h.user;
  const canEditThis = canManage || viewer?.id === userId;
  // Cache-bust the avatar every time modal is opened / photo changes.
  const photoSrc = u.profile_photo_url
    ? (u.profile_photo_url.startsWith("data:") ? u.profile_photo_url
       : `${u.profile_photo_url}${u.profile_photo_url.includes("?") ? "&" : "?"}t=${Date.now()}`)
    : null;

  const outerCls = inline
    ? ""
    : "fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4";
  const innerCls = inline
    ? "bg-white border border-zinc-200 rounded-sm w-full"
    : "bg-white border border-zinc-200 rounded-sm w-[95vw] max-w-4xl max-h-[92vh] overflow-y-auto";
  return (
    <div className={outerCls} onClick={inline ? undefined : onClose} data-testid="employee-detail-modal">
      <div className={innerCls} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {photoSrc ? (
              <img key={photoSrc} src={photoSrc} alt="" className="w-14 h-14 rounded-full object-cover" data-testid="employee-photo" />
            ) : (
              <EmployeeInitials name={u.name} />
            )}
            <div>
              <div className="text-sm font-semibold" data-testid="employee-name">{u.name}</div>
              <div className="text-xs text-zinc-500">{u.title || "—"}</div>
              <div className="mt-1"><AvatarControls user={u} canEdit={canEditThis} onUpdated={() => { load(); onChanged?.(); }} /></div>
            </div>
          </div>
          <div className="flex gap-2">
            {canManage && (
              <button
                data-testid="trigger-pip-btn"
                onClick={() => setShowPip(true)}
                className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-sm hover:bg-amber-700"
              >
                Kick off PIP
              </button>
            )}
            {!inline && (
              <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-lg">×</button>
            )}
          </div>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-4">
          <ContactBlock email={u.email} phone={u.phone} />
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">KPA reviews ({h.reviews.length})</div>
            {h.reviews.length === 0 ? <div className="text-sm text-zinc-400">No reviews yet.</div> : (
              <ul className="space-y-1.5">
                {h.reviews.map((r) => (
                  <li key={r.id} className="text-sm flex justify-between border-t border-zinc-100 pt-1.5 first:border-0 first:pt-0">
                    <Link to={`/reviews/${r.id}`} className="hover:underline">{r.template_name} · {r.quarter} {r.year}</Link>
                    <span className="text-xs text-zinc-500">{r.stage} · {r.final_average ?? "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border border-zinc-200 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Signed documents ({h.signed_policies.length})</div>
            {h.signed_policies.length === 0 ? <div className="text-sm text-zinc-400">Nothing signed yet.</div> : (
              <ul className="space-y-1.5">
                {h.signed_policies.map((p) => (
                  <li key={p.id} className="text-sm flex justify-between border-t border-zinc-100 pt-1.5 first:border-0 first:pt-0">
                    <Link to={`/policies/${p.id}`} className="hover:underline">
                      <span className="text-[9px] uppercase mr-1 text-zinc-400">{p.doc_type}</span>{p.name}
                    </Link>
                    <span className="text-xs text-zinc-500">{(p.signed_at || "").split("T")[0]}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {h.pending_policies.length > 0 && (
            <div className="border border-zinc-200 rounded-sm p-4 md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Documents awaiting signature ({h.pending_policies.length})</div>
              <ul className="space-y-1.5">
                {h.pending_policies.map((p) => (
                  <li key={p.id} className="text-sm border-t border-zinc-100 pt-1.5 first:border-0 first:pt-0">
                    <Link to={`/policies/${p.id}`} className="hover:underline">
                      <span className="text-[9px] uppercase mr-1 text-zinc-400">{p.doc_type}</span>{p.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Skills Matrix — user rates & attaches certs */}
          <SkillsSection userId={userId} canEdit={canEditThis} />

          {/* My Documents — admins/managers upload named files (warnings etc.) */}
          <DocumentsSection userId={userId} canManage={canManage} />

          <div className="border border-zinc-200 rounded-sm p-4 md:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Performance Improvement Plans ({h.pips.length})</div>
            {h.pips.length === 0 ? <div className="text-sm text-zinc-400">No PIPs on file.</div> : (
              <ul className="space-y-1.5">
                {h.pips.map((p) => (
                  <li key={p.id} className="text-sm flex justify-between border-t border-zinc-100 pt-1.5 first:border-0 first:pt-0">
                    <span>{p.title}</span>
                    <span className="text-xs text-zinc-500">
                      {p.status}{p.outcome ? ` · ${p.outcome}` : ""} {p.review_date ? `· review ${p.review_date}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      {showPip && (
        <PIPCreateModal
          employeeId={userId}
          employeeName={u.name}
          buId={buId}
          onClose={() => setShowPip(false)}
          onCreated={() => { setShowPip(false); load(); onChanged?.(); toast.success("PIP created as draft"); }}
        />
      )}
    </div>
  );
}


function PIPCreateModal({ employeeId, employeeName, buId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [support, setSupport] = useState("Weekly 1-on-1 coaching, access to training resources, buddy pairing.");
  const [cadence, setCadence] = useState("weekly");
  const [reviewDate, setReviewDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 60);
    return d.toISOString().slice(0, 10);
  });
  const [goals, setGoals] = useState([
    { text: "", metric: "", target: "" },
    { text: "", metric: "", target: "" },
    { text: "", metric: "", target: "" },
  ]);
  const [saving, setSaving] = useState(false);

  const updateGoal = (i, patch) => setGoals((g) => g.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const addGoal = () => setGoals((g) => [...g, { text: "", metric: "", target: "" }]);
  const removeGoal = (i) => setGoals((g) => g.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!title.trim()) return toast.error("Give the PIP a title");
    const cleanGoals = goals.filter((g) => (g.text || "").trim());
    if (cleanGoals.length === 0) return toast.error("Add at least one measurable goal");
    setSaving(true);
    try {
      await api.post("/pips", {
        employee_id: employeeId,
        business_unit_id: buId,
        title: title.trim(),
        root_cause: rootCause.trim(),
        support_offered: support.trim(),
        checkin_cadence: cadence,
        review_date: reviewDate,
        goals: cleanGoals,
      });
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="pip-create-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-[95vw] max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">
          Kick off PIP for {employeeName}
        </div>
        <div className="p-5 space-y-4">
          <input data-testid="pip-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Plan title (e.g. Q1 Sales Performance Recovery)"
            className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Root cause / context</div>
            <textarea data-testid="pip-root-cause" value={rootCause} onChange={(e) => setRootCause(e.target.value)}
              rows={2} placeholder="What performance gap prompted this plan?"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Measurable goals</div>
            <div className="space-y-2">
              {goals.map((g, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input data-testid={`pip-goal-text-${i}`} value={g.text} onChange={(e) => updateGoal(i, { text: e.target.value })}
                    placeholder="Goal (e.g. Close 5 deals per week)" className="col-span-6 border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
                  <input value={g.metric} onChange={(e) => updateGoal(i, { metric: e.target.value })}
                    placeholder="Metric" className="col-span-3 border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
                  <input value={g.target} onChange={(e) => updateGoal(i, { target: e.target.value })}
                    placeholder="Target" className="col-span-2 border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
                  <button onClick={() => removeGoal(i)} className="col-span-1 text-red-600 hover:text-red-800 text-lg">×</button>
                </div>
              ))}
            </div>
            <button onClick={addGoal} className="mt-2 text-xs text-blue-600 hover:underline">+ Add another goal</button>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Support offered</div>
            <textarea value={support} onChange={(e) => setSupport(e.target.value)}
              rows={2} className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Check-in cadence</div>
              <select value={cadence} onChange={(e) => setCadence(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white">
                <option value="weekly">Weekly</option>
                <option value="bi-weekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Review date</div>
              <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
            </div>
          </div>
          <div className="text-xs text-zinc-500 bg-zinc-50 p-3 border border-zinc-200 rounded-sm">
            After creation the PIP is a <b>draft</b>. Submit it to the BU Owner who approves and sends
            it to the employee to acknowledge. Once acknowledged the PIP becomes <b>active</b> until
            the review date, when it&apos;s closed with an outcome (Improved / Extended / Terminated).
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button data-testid="pip-save-btn" onClick={save} disabled={saving}
              style={{ background: "var(--brand-primary)" }}
              className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 disabled:opacity-60">
              {saving ? "Saving…" : "Create PIP (Draft)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function BUOrganogram({ buId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/business-units/${buId}/organogram`); setData(data); }
      catch (e) { setErr(formatApiError(e)); }
    })();
  }, [buId]);
  if (err) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3">{err}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading org chart…</div>;
  return (
    <div data-testid="bu-organogram">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">Organogram · {data.user_count} people</div>
      {data.roots.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-10 text-center">
          No users assigned to this BU yet. Sync from Microsoft or add users in Settings.
        </div>
      ) : (
        <div className="overflow-auto border border-zinc-200 bg-white rounded-sm p-6">
          <div className="flex gap-6 items-start">
            {data.roots.map((r) => <OrgNode key={r.id} node={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}


function BUPIPs({ buId, canCreate, canApprove }) {
  const [pips, setPips] = useState([]);
  const load = async () => {
    try { const { data } = await api.get(`/pips?business_unit_id=${buId}`); setPips(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [buId]);

  const act = async (pid, action, body) => {
    try {
      await api.post(`/pips/${pid}/${action}`, body || {});
      toast.success(`PIP ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeWith = async (pid) => {
    const outcome = prompt("Close outcome: improved / extended / terminated");
    if (!outcome) return;
    if (!["improved", "extended", "terminated"].includes(outcome)) return toast.error("Invalid outcome");
    const notes = prompt("Closing notes (optional)") || "";
    await act(pid, "close", { outcome, notes });
  };

  return (
    <div data-testid="bu-pip-list">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Performance Improvement Plans</div>
          <div className="text-xs text-zinc-500">Trigger a PIP from an employee&apos;s tile in the Employees tab.</div>
        </div>
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Review date</th>
              <th className="text-left px-4 py-3">Outcome</th>
              <th className="px-4 py-3 w-56"></th>
            </tr>
          </thead>
          <tbody>
            {pips.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">No PIPs yet. Kick one off from the Employees tab.</td></tr>
            )}
            {pips.map((p) => (
              <tr key={p.id} className="border-t border-zinc-200">
                <td className="px-4 py-3 font-medium">{p.employee_name}</td>
                <td className="px-4 py-3">{p.title}</td>
                <td className="px-4 py-3">
                  <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-zinc-50 text-zinc-700 border-zinc-200">{p.status}</span>
                </td>
                <td className="px-4 py-3 text-zinc-500">{p.review_date || "—"}</td>
                <td className="px-4 py-3 text-zinc-500">{p.outcome || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {p.status === "draft" && canCreate && (
                    <button data-testid={`pip-submit-${p.id}`} onClick={() => act(p.id, "submit")}
                      className="text-xs bg-amber-600 text-white px-3 py-1 rounded-sm hover:bg-amber-700 mr-2">Submit for approval</button>
                  )}
                  {p.status === "pending_owner" && canApprove && (
                    <button data-testid={`pip-approve-${p.id}`} onClick={() => act(p.id, "approve")}
                      className="text-xs bg-emerald-700 text-white px-3 py-1 rounded-sm hover:bg-emerald-800 mr-2">Approve</button>
                  )}
                  {p.status === "active" && canCreate && (
                    <button data-testid={`pip-close-${p.id}`} onClick={() => closeWith(p.id)}
                      className="text-xs bg-zinc-700 text-white px-3 py-1 rounded-sm hover:bg-zinc-900">Close plan</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function BUScenarios({ buId, canPublish }) {
  const [scenarios, setScenarios] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/scenarios?business_unit_id=${buId}`); setScenarios(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [buId]);

  const create = async () => {
    const name = prompt("Name your scenario (e.g. Q3 restructure)");
    if (!name) return;
    try {
      const { data } = await api.post("/scenarios", { name, business_unit_id: buId, description: "", moves: [] });
      setEditing(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publish = async (sid) => {
    if (!confirm("Publish this scenario? All planned moves will be applied to live user records.")) return;
    try { const { data } = await api.post(`/scenarios/${sid}/publish`); toast.success(`Applied ${data.applied} move(s)`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (sid) => {
    if (!confirm("Delete this scenario?")) return;
    try { await api.delete(`/scenarios/${sid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="bu-scenarios">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">What-if scenarios</div>
          <div className="text-xs text-zinc-500">Plan restructures, promotions and moves before they go live. Publish to apply changes.</div>
        </div>
        {canPublish && (
          <button data-testid="scenario-new-btn" onClick={create}
            style={{ background: "var(--brand-primary)" }}
            className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90">
            + New scenario
          </button>
        )}
      </div>
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Moves</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="px-4 py-3 w-72"></th>
            </tr>
          </thead>
          <tbody>
            {scenarios.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">No scenarios yet. Create one to plan a restructure.</td></tr>
            )}
            {scenarios.map((s) => (
              <tr key={s.id} className="border-t border-zinc-200">
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3">
                  <span className="inline-block text-[10px] uppercase px-2 py-0.5 rounded-sm border bg-zinc-50 text-zinc-700 border-zinc-200">{s.status}</span>
                </td>
                <td className="px-4 py-3 tabular-nums">{(s.moves || []).length}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{(s.created_at || "").split("T")[0]}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {s.status === "draft" && (
                    <>
                      <button data-testid={`scenario-edit-${s.id}`} onClick={() => setEditing(s)}
                        className="text-xs text-blue-600 hover:underline mr-3">Plan moves</button>
                      {canPublish && (
                        <button data-testid={`scenario-publish-${s.id}`} onClick={() => publish(s.id)}
                          className="text-xs bg-emerald-700 text-white px-3 py-1 rounded-sm hover:bg-emerald-800 mr-2">
                          Publish
                        </button>
                      )}
                    </>
                  )}
                  {canPublish && (
                    <button onClick={() => del(s.id)} className="text-red-600 hover:text-red-800" title="Delete">
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <ScenarioEditor scenario={editing} buId={buId} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}


function ScenarioEditor({ scenario, buId, onClose, onSaved }) {
  const [name, setName] = useState(scenario.name || "");
  const [description, setDescription] = useState(scenario.description || "");
  const [moves, setMoves] = useState(scenario.moves || []);
  const [buUsers, setBuUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: u }, { data: d }] = await Promise.all([
          api.get(`/business-units/${buId}/employees`),
          api.get(`/business-units/${buId}/departments`).catch(() => ({ data: [] })),
        ]);
        setBuUsers(u);
        setDepts(d);
      } catch { /* silent */ }
    })();
  }, [buId]);

  const addMove = (action) => {
    setMoves([...moves, { action, user_id: "", new_manager_id: "", new_title: "", new_department_id: "", vacant_role_title: "", vacant_reports_to: "", note: "" }]);
  };
  const updateMove = (i, patch) => setMoves((m) => m.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const removeMove = (i) => setMoves((m) => m.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/scenarios/${scenario.id}`, { name, business_unit_id: buId, description, moves });
      toast.success("Scenario saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const userName = (id) => buUsers.find((u) => u.id === id)?.name || "?";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="scenario-editor">
      <div className="bg-white border border-zinc-200 rounded-sm w-[95vw] max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <input value={name} onChange={(e) => setName(e.target.value)}
            data-testid="scenario-name-input"
            className="text-sm font-semibold border-0 focus:outline-none flex-1 mr-4" placeholder="Scenario name" />
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-lg">×</button>
        </div>
        <div className="p-5 space-y-4">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            placeholder="Describe the goal of this scenario…"
            className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => addMove("reassign_manager")} className="text-xs border border-zinc-300 px-2 py-1 rounded-sm hover:bg-zinc-50">+ Reassign manager</button>
            <button onClick={() => addMove("promote")} className="text-xs border border-zinc-300 px-2 py-1 rounded-sm hover:bg-zinc-50">+ Promote</button>
            <button onClick={() => addMove("change_profile")} className="text-xs border border-zinc-300 px-2 py-1 rounded-sm hover:bg-zinc-50">+ Move to profile</button>
            <button onClick={() => addMove("add_vacant")} className="text-xs border border-zinc-300 px-2 py-1 rounded-sm hover:bg-zinc-50">+ Add vacant role</button>
            <button onClick={() => addMove("offboard")} className="text-xs border border-zinc-300 px-2 py-1 rounded-sm hover:bg-zinc-50">+ Offboard</button>
          </div>
          <div className="space-y-2">
            {moves.map((m, i) => (
              <div key={i} className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase font-semibold text-zinc-600">{m.action.replace("_", " ")}</span>
                  <button onClick={() => removeMove(i)} className="text-red-600 hover:text-red-800 text-xs">Remove</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {m.action !== "add_vacant" && (
                    <select value={m.user_id || ""} onChange={(e) => updateMove(i, { user_id: e.target.value })}
                      className="border border-zinc-300 px-2 py-1.5 rounded-sm text-sm bg-white">
                      <option value="">— Pick employee —</option>
                      {buUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  )}
                  {m.action === "reassign_manager" && (
                    <select value={m.new_manager_id || ""} onChange={(e) => updateMove(i, { new_manager_id: e.target.value })}
                      className="border border-zinc-300 px-2 py-1.5 rounded-sm text-sm bg-white">
                      <option value="">— Pick new manager —</option>
                      {buUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  )}
                  {m.action === "promote" && (
                    <input value={m.new_title || ""} onChange={(e) => updateMove(i, { new_title: e.target.value })}
                      placeholder="New title" className="border border-zinc-300 px-2 py-1.5 rounded-sm text-sm" />
                  )}
                  {m.action === "change_profile" && (
                    <select value={m.new_department_id || ""} onChange={(e) => updateMove(i, { new_department_id: e.target.value })}
                      className="border border-zinc-300 px-2 py-1.5 rounded-sm text-sm bg-white">
                      <option value="">— Pick Position Profile —</option>
                      {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                  {m.action === "add_vacant" && (
                    <>
                      <input value={m.vacant_role_title || ""} onChange={(e) => updateMove(i, { vacant_role_title: e.target.value })}
                        placeholder="Vacant role title" className="border border-zinc-300 px-2 py-1.5 rounded-sm text-sm" />
                      <select value={m.vacant_reports_to || ""} onChange={(e) => updateMove(i, { vacant_reports_to: e.target.value })}
                        className="col-span-2 border border-zinc-300 px-2 py-1.5 rounded-sm text-sm bg-white">
                        <option value="">— Reports to (optional) —</option>
                        {buUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    </>
                  )}
                  <input value={m.note || ""} onChange={(e) => updateMove(i, { note: e.target.value })}
                    placeholder="Note (optional)" className="col-span-2 border border-zinc-300 px-2 py-1.5 rounded-sm text-sm" />
                </div>
                {m.user_id && <div className="text-[10px] text-zinc-500 mt-1">→ {userName(m.user_id)}</div>}
              </div>
            ))}
            {moves.length === 0 && (
              <div className="text-sm text-zinc-400 text-center py-8">Add moves using the buttons above.</div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button data-testid="scenario-save-btn" onClick={save} disabled={saving}
              style={{ background: "var(--brand-primary)" }}
              className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 disabled:opacity-60">
              {saving ? "Saving…" : "Save scenario"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



function MyActivity({ buId, userId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Server auto-scopes to actor=me for non-super-admins; passing actor_id is a no-op
      // for them, but harmless. It's set explicitly so a Super Admin viewing their own
      // BU My Activity tab also sees just themselves (not everyone).
      if (userId) params.set("actor_id", userId);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      params.set("limit", "500");
      const { data } = await api.get(`/audit?${params.toString()}`);
      setEvents(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, buId]);

  return (
    <div>
      <div className="mb-4 flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">My activity in this BU</div>
          <div className="text-xs text-zinc-500">Every action you took, most recent first. Immutable.</div>
        </div>
        <div className="flex-1" />
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="block mt-1 border border-zinc-300 px-2 py-1.5 rounded-sm text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="block mt-1 border border-zinc-300 px-2 py-1.5 rounded-sm text-xs" />
        </div>
        <button
          onClick={load}
          style={{ background: "var(--brand-primary)" }}
          className="text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:opacity-90"
        >
          Apply
        </button>
      </div>
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3 w-44">When</th>
              <th className="text-left px-5 py-3 w-56">Action</th>
              <th className="text-left px-5 py-3">Summary</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="px-5 py-12 text-center text-zinc-400 text-sm">Loading…</td></tr>
            ) : events.length === 0 ? (
              <tr><td colSpan={3} className="px-5 py-12 text-center text-zinc-500 text-sm">Nothing recorded yet.</td></tr>
            ) : events.map((e) => (
              <tr key={e.id} className="border-t border-zinc-200">
                <td className="px-5 py-3 text-zinc-600 text-xs tabular-nums">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-xs font-medium text-zinc-700">{e.action.replace(/\./g, " · ")}</td>
                <td className="px-5 py-3 text-zinc-700">{e.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

